import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Building2, Stethoscope, ArrowRight, Clock, ShieldCheck, PhoneMissed, CircleCheck, MessageSquareDot, CalendarCheck } from "lucide-react";
export default function RealEstateHealthcare() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="real-estate-healthcare" ref={sectionRef} className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-indigo-900/10 rounded-full blur-3xl translate-y-1/3 -translate-x-1/3" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className={`text-center max-w-3xl mx-auto mb-20 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">
            High-Stakes Industries
          </h2>
          <p className="text-xl text-muted-foreground font-light">
            Tailored AI voice solutions for sectors where every call matters. 
            From closing property deals to managing patient care, MollyCall.ai delivers enterprise-grade reliability.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-32">
          <div className={`order-1 lg:order-1 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-lg bg-primary/10 text-primary">
                <Building2 className="size-8" />
              </div>
              <h3 className="text-3xl font-bold">Real Estate</h3>
            </div>
            
            <h4 className="text-2xl font-semibold mb-4 text-white">
              Instant Inquiry Response
            </h4>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              In real estate, speed is everything. Our AI agents qualify leads instantly, 
              schedule viewings, and ensure you never miss a potential buyer, even during open houses.
            </p>

            <div className="mb-8 border-l-4 border-primary pl-6 py-2 bg-primary/5 rounded-r-lg">
              <p className="text-lg italic font-serif text-gray-300 mb-2">
                "The AI voice agents are incredibly natural and have increased our appointment bookings."
              </p>
              <p className="text-sm font-bold text-primary uppercase tracking-wider">
                — Michael R., Operations Manager, RealEstatePro
              </p>
            </div>

            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="rounded-full px-8">
                <Link to="/industries">
                  Explore Real Estate Solutions <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
            </div>
          </div>

          <div className={`order-2 lg:order-2 grid gap-6 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            {}
            <Card className="bg-red-950/10 border-red-900/30 backdrop-blur-sm relative overflow-hidden group hover:border-red-900/50 transition-colors">
              <div className="absolute top-0 left-0 w-1 h-full bg-red-500/50" />
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-red-200 flex items-center gap-2">
                    <PhoneMissed className="size-5" /> The Problem
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-red-100/80">
                  Leads go cold within 5 minutes. Missed calls during showings result in lost commissions and frustrated clients.
                </p>
              </CardContent>
            </Card>

            {}
            <Card className="bg-primary/10 border-primary/30 backdrop-blur-sm relative overflow-hidden group hover:border-primary/50 transition-colors shadow-lg shadow-primary/5">
              <div className="absolute top-0 left-0 w-1 h-full bg-primary" />
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-indigo-100 flex items-center gap-2">
                    <CircleCheck className="size-5 text-primary" /> The Solution
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-indigo-50/80">
                  Instant 24/7 AI response captures every inquiry immediately, qualifies buyer intent, and syncs directly to your CRM.
                </p>
              </CardContent>
            </Card>

            {}
            <div className="relative h-48 rounded-xl overflow-hidden mt-2 border border-white/10">
               <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572085/img/real_estate_agent_phone.png" alt="Real estate agent checking leads on phone" className="w-full h-full object-cover opacity-60 hover:opacity-80 transition-opacity duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
              <div className="absolute bottom-4 left-4">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/20 text-primary text-xs font-medium border border-primary/20 backdrop-blur-md">
                  <Clock className="size-3" /> Response Time: &lt; 2s
                </span>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className={`order-2 lg:order-1 grid gap-6 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
             {}
             <div className="relative h-48 rounded-xl overflow-hidden mb-2 border border-white/10">
               <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572085/img/healthcare_professional_tablet.png" alt="Healthcare professional managing schedule" className="w-full h-full object-cover opacity-60 hover:opacity-80 transition-opacity duration-500" />
              <div className="absolute inset-0 bg-gradient-to-b from-background to-transparent" />
              <div className="absolute top-4 left-4">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-medium border border-emerald-500/20 backdrop-blur-md">
                  <ShieldCheck className="size-3" /> HIPAA Compliant
                </span>
              </div>
            </div>

            {}
            <Card className="bg-orange-950/10 border-orange-900/30 backdrop-blur-sm relative overflow-hidden group hover:border-orange-900/50 transition-colors">
              <div className="absolute top-0 left-0 w-1 h-full bg-orange-500/50" />
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-orange-200 flex items-center gap-2">
                    <CalendarCheck className="size-5" /> The Problem
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-orange-100/80">
                  Overwhelmed front desks lead to long hold times, missed appointments, and patient dissatisfaction.
                </p>
              </CardContent>
            </Card>

            {}
            <Card className="bg-emerald-950/10 border-emerald-900/30 backdrop-blur-sm relative overflow-hidden group hover:border-emerald-900/50 transition-colors shadow-lg shadow-emerald-900/5">
              <div className="absolute top-0 left-0 w-1 h-full bg-emerald-500" />
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-emerald-100 flex items-center gap-2">
                    <Stethoscope className="size-5 text-emerald-500" /> The Solution
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-emerald-50/80">
                  Secure, HIPAA-compliant AI scheduling handles intake, reminders, and FAQs without human intervention.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className={`order-1 lg:order-2 transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-lg bg-emerald-500/10 text-emerald-500">
                <Stethoscope className="size-8" />
              </div>
              <h3 className="text-3xl font-bold">Healthcare</h3>
            </div>
            
            <h4 className="text-2xl font-semibold mb-4 text-white">
              Secure & Compassionate Care
            </h4>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Patient trust starts with the first call. Our AI agents provide empathetic, 
              secure communication for appointment booking, prescription refills, and triage support.
            </p>

            <div className="mb-8 border-l-4 border-emerald-500 pl-6 py-2 bg-emerald-500/5 rounded-r-lg">
              <p className="text-lg italic font-serif text-gray-300 mb-2">
                "Automating our appointment reminders reduced no-shows by 35% in the first month alone."
              </p>
              <p className="text-sm font-bold text-emerald-500 uppercase tracking-wider">
                — Clinic Director, Regional Health Partners
              </p>
            </div>

            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="rounded-full px-8 bg-emerald-600 hover:bg-emerald-700 text-white border-none shadow-emerald-900/20">
                <Link to="/industries">
                  View Healthcare Solutions <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}